import { useEffect, useState } from "react";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { emailsAPI } from "@/lib/api";
import { normalizeEmail } from "@/lib/email";
import { t } from "@/lib/locale";

/**
 * Champ e-mail vérifié EN TEMPS RÉEL : format, faute de frappe (suggestion en
 * un clic) et existence réelle du domaine (MX). Une adresse fausse = un client
 * qui paie et ne reçoit jamais sa confirmation.
 */
export const EmailField = ({ value, onChange }) => {
    const [state, setState] = useState({ status: "idle", suggestion: null, domain: null });

    useEffect(() => {
        const raw = (value || "").trim();
        if (!raw) { setState({ status: "idle", suggestion: null, domain: null }); return; }
        if (!raw.includes("@") && !normalizeEmail(raw)) {
            setState({ status: "typing", suggestion: null, domain: null });
            return;
        }
        setState((s) => ({ ...s, status: "checking" }));
        const timer = setTimeout(async () => {
            try {
                const r = await emailsAPI.validate(raw);
                setState({
                    status: r.valid ? "valid" : r.reason,
                    suggestion: r.suggestion || null,
                    domain: r.domain || null,
                });
            } catch {
                // Serveur injoignable : on retombe sur la validation locale.
                setState({ status: normalizeEmail(raw) ? "valid" : "format", suggestion: null, domain: null });
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [value]);

    const applySuggestion = () => {
        if (state.suggestion) onChange(state.suggestion);
    };

    const bad = ["format", "domain"].includes(state.status);
    return (
        <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="co-email">Email</Label>
            <div className="relative">
                <Input
                    id="co-email"
                    data-testid="email-input"
                    required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    aria-invalid={bad}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`pr-9 ${bad ? "border-destructive focus-visible:ring-destructive" : state.status === "valid" ? "border-success" : ""}`}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    {state.status === "checking" && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    {state.status === "valid" && <Check data-testid="email-valid-icon" className="h-4 w-4 text-success" />}
                    {bad && <AlertCircle className="h-4 w-4 text-destructive" />}
                </span>
            </div>

            {state.status === "typo" && state.suggestion && (
                <p data-testid="email-suggestion" className="text-xs text-amber-700">
                    {t("Vouliez-vous dire")}{" "}
                    <button
                        type="button"
                        onClick={applySuggestion}
                        data-testid="email-suggestion-apply"
                        className="font-semibold underline underline-offset-2 hover:no-underline"
                    >
                        {state.suggestion}
                    </button>{" "}
                    ? {t("Cliquez pour corriger.")}
                </p>
            )}
            {state.status === "format" && (
                <p data-testid="email-error" className="text-xs text-destructive">
                    {t("Adresse e-mail incomplète — exemple : nom@gmail.com")}
                </p>
            )}
            {state.status === "domain" && (
                <p data-testid="email-error" className="text-xs text-destructive">
                    {t("Ce domaine ne reçoit pas d'e-mails")} ({state.domain}) — {t("vérifiez l'orthographe.")}
                </p>
            )}
            {state.status === "valid" && (
                <p data-testid="email-ok" className="text-xs text-success">
                    {t("Adresse vérifiée — votre confirmation arrivera ici.")}
                </p>
            )}
            {["idle", "typing", "checking"].includes(state.status) && (
                <p className="text-xs text-muted-foreground">
                    {t("Votre confirmation de commande et le suivi sont envoyés à cette adresse.")}
                </p>
            )}
        </div>
    );
};
